import create from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  getDocumentFromParams,
  getJwt,
  getUrlParamsObject,
} from '../utils/browser';
import {
  defaultConfiguration,
  defaultItemValues,
  defaultMasks,
  defaultSlider,
} from '../constants/constants';
import { apiActions } from './api';
import { boxesActions } from './boxes';
import { configurationActions } from './configuration';
import { masksActions } from './masks';
import { pdfActions } from './pdf';
import { styleActions } from './style';
import { titlesActions } from './titles';
import { browserActions } from './browser';
import { containerActions } from './containers';
import { columnsActions } from './columns';
import { sectionsActions } from './sections';
import { variablesActions } from './variables';
import { variableGroupsActions } from './variableGroups';
import { variableOrderArrayActions } from './variableOrderArray';
import { triggersActions } from './triggers';
import produce from 'immer';
import { pagesPreviewActions } from './pagesPreview';
import { searchActions } from './search';
import { displayedVariablesActions } from './displayedVariables';
import { collabActions } from './collab';
import { undoActions } from './undo';
import { sectionLoadingHolder } from '../collab/structureLocks';
import { zoomActions } from './zoom';
import { readStoredZoom } from '../utils/zoom';

const currentDocument = getDocumentFromParams(getUrlParamsObject());
const urlParams = getUrlParamsObject();
const currentProposal = {
  id: currentDocument.type === 'template' ? null : urlParams.pid || null,
};
const hasDocumentToLoad =
  currentDocument.type === 'template'
    ? !!currentDocument.id
    : !!currentProposal.id;

const defaultStoreValues = {
  loadingData: false,
  // Indique si le document (template/proposition) est en cours de (re)chargement / application
  // Utilisé pour afficher le loader global et bloquer les interactions
  isDocumentLoading: hasDocumentToLoad,
  currentDocument,
  currentTemplate: null,
  currentProposal,
  landscape: false,
  PDFRenderState: false,
  previewRenderState: false,
  pagesPreview: [],
  // Container d'où part le chapitre affiché dans l'aperçu. Distinct de
  // firstVisibleContainerId : l'éditeur reste monté sous l'aperçu et son scroll
  // (recalé par virtua quand on retire un espace au-dessus de lui) ne doit pas
  // faire changer l'aperçu de chapitre.
  previewAnchorContainerId: null,
  // Formulaire incomplet au moment de l'aperçu (filigrane « valeurs
  // manquantes ») ; undefined en modèle.
  previewFormIncomplete: undefined,
  masks: defaultMasks,
  selectedBoxId: null,
  selectedContainer: null,
  selectedContainerIds: [],
  activeContainerId: null,
  // Cellule que l'utilisateur vient de désigner sur le canvas, pour que le
  // panel de disposition s'ouvre sur le réglage correspondant. `nonce` : sans
  // lui, recliquer la même cellule ne rejouerait pas l'ouverture.
  layoutFocus: null,
  groupSelection: [],
  tables: [],
  splittableTables: [],
  // Tableaux dont le découpage par zone se règle dans le Drawer (liste du serveur).
  zoneSplitTables: [],
  templates: [],
  sections: [],
  // Première réponse de getTemplates reçue (même en échec) : avant, une liste
  // vide veut dire « en cours de chargement », pas « aucun document ».
  templatesLoaded: false,
  folders: [],
  hiddenTemplates: [],
  hiddenSections: [],
  proposals: [],
  proposalList: true,
  user: getJwt(),
  // pages: [newPage()],
  configuration: defaultConfiguration,
  summaryConfiguration: defaultItemValues.summary.content.configuration,
  copies: [],
  gridConfiguration: defaultItemValues,
  boxes: [],
  variables: {},
  defaultVariablesApi: null,
  displayMargins: false,
  // Marges de page tracées par défaut pendant l'édition du décor de page ;
  // cachées à la demande (cf. showsPageBackgroundMargins).
  hidePageBackgroundMargins: false,
  boxIsMovable: false,
  slider: defaultSlider,
  maskEditing: [],
  containers: [],
  columns: [],
  // Containers imbriqués : les "lignes" empilées à l'intérieur d'une colonne
  // (cf. column.rowContainerIds). Même forme qu'un container, mais tableau
  // séparé : `containers` est indexé positionnellement (ordre de lecture du
  // document, cf. titles.js/sections.js/getRangeContainerIds) et n'accepte que
  // du top-level.
  rowContainers: [],
  eventManagerEnabled: true,
  variableGroups: [],
  boxVariableSelectedId: null,
  // Occurrence de variable cliquée dans le document : { boxId, entityKey,
  // variableKey }. Une même variable pouvant être insérée plusieurs fois, la
  // clé seule ne suffit pas pour régler l'affichage d'une insertion précise
  // (cf. VariableLineStyleControl). NON persisté.
  variableOccurrenceSelected: null,
  // Box tableau paramétrable dont le Drawer de configuration est ouvert (ou null).
  tableConfigBoxId: null,
  // Case à cocher / choix dont le drawer « Plus de paramètres » est ouvert (ou
  // null) : le panel de droite n'expose que le nom et la liste des réponses, le
  // reste des réglages de formulaire vit dans le drawer. NON persisté.
  variableFormDrawerBoxId: null,
  // Box organigramme dont la modale d'édition est ouverte (ou null). L'état ne peut
  // pas vivre dans le composant : sélectionner une box échange Dragger <-> Resizer
  // (BoxWrapper), ce qui démonte le sous-arbre et jetterait l'ouverture faite par
  // le même clic. NON persisté.
  orgChartModalBoxId: null,
  // Réponse brute du backend par box tableau (clé = boxId). NON persisté (état
  // client) : sert à réappliquer les changements de PRÉSENTATION (renommer/
  // masquer/réordonner/largeur colonne) en local, sans refetch. Voir applyTableConfig.
  tableRawCache: {},
  // État des captures planning par tranche de tableau (cf. utils/planningScreenshots).
  // NON persisté : état serveur, relu à chaque lecture d'un tableau planning.
  planningScreenshotStatus: {},
  // Capacités BPU renvoyées par /tables (ex. { additional_work: bool }).
  bpuCapabilities: null,
  shouldScrollToVariableInFormViewer: false,
  loaderPdfToImage: false,
  // PDF >= 15 Mo en attente de décision de compression : { pdfUrl, sizeBytes, documentId }
  pdfCompression: null,
  isPdfCompressing: false,
  tempContainerPdfToImage: null,
  variableOrderArray: [],
  triggers: {},
  sectionsToActivate: [],
  hasOpportunityOption: false,
  // Identité du document en cours (opportunité/offre) affichée dans la navbar :
  // { documentName, opportunityName, opportunityChrono, offerName, offerChrono,
  // hidden } — `hidden` : l'opportunité/offre a été cachée, le document ne sera
  // plus listé une fois l'éditeur fermé (rafraîchi à chaque sauvegarde).
  // Renseignée à l'application d'une proposition, remise à null hors document.
  documentIdentity: null,
  hasCostingActivated: false,
  hasOpportunityFA: false,
  hasCompanyWorkFA: false,
  zoneNames: [],
  zoneIcon: 'fa-kit fa-solid-bars-progress-gear',
  itemsChangedByTriggers: [],
  displayedVariables: {},
  showAllVariables: false, // Filtre pour afficher toutes les variables ou seulement celles avec erreurs
  firstVisibleContainerId: null,
  recursiveModelFields: {},
  recursiveSectionRelationships: {},
  recursiveModelLabels: {},
  loadedRecursiveSections: [],
  recursiveUsedFieldKeys: {},
  // File d'attente : un modèle d'offre peut porter plusieurs placeholders
  // RECURSIVE_MODEL, chacun pouvant demander un choix de modèle à la création.
  // Un slot unique écrasait les précédents → une seule modale, les autres
  // placeholders restaient gris à jamais. La modale dépile une entrée à la fois.
  pendingRecursiveModelChoices: [],
  // Zoom par-utilisateur (localStorage) : runtime-only, jamais synchronisé ni
  // persisté, jamais réinitialisé au changement de document (cf. utils/zoom.js)
  zoomLevel: readStoredZoom(),
  canScrollX: false,
  panModeActive: false,
  // Do/undo local (per-user, en mémoire). Piles d'entrées { forward, inverse,
  // focus } ; jamais diffusées ni persistées. Cf. src/undo/undoCapture.js.
  undoStack: [],
  redoStack: [],
  // Compteur incrémenté pour demander l'ouverture du configurateur depuis
  // n'importe où (ex: onglet "Paramètres" du panel gauche). La Navbar l'observe
  // et applique sa logique collab (claim du verrou configurateur). Cf. Navbar.jsx.
  configuratorOpenRequestTick: 0,
  // Incrémenté après un upload d'image dans l'éditeur : force le picker de la
  // banque à refetch (la nouvelle image est rangée dans le dossier auto du doc).
  mediaBankVersion: 0,
  // Même principe, après l'enregistrement d'une forme composée dans la banque.
  shapeBankVersion: 0,
  // Même principe, après l'enregistrement d'une carte comme modèle.
  cardBankVersion: 0,
};

const store = (set, get) => ({
  ...defaultStoreValues,
  ...apiActions(set, get),
  ...boxesActions(set, get),
  ...configurationActions(set, get),
  ...masksActions(set, get),
  // ...pagesActions(set, get),
  ...pdfActions(set, get),
  ...styleActions(set, get),
  ...titlesActions(set, get),
  ...browserActions(set, get),
  ...sectionsActions(set, get),
  ...containerActions(set, get),
  ...columnsActions(set, get),
  ...variablesActions(set, get),
  ...variableGroupsActions(set, get),
  ...variableOrderArrayActions(set, get),
  ...triggersActions(set, get),
  ...pagesPreviewActions(set, get),
  ...searchActions(set, get),
  ...displayedVariablesActions(set, get),
  ...collabActions(set, get),
  ...undoActions(set, get),
  ...zoomActions(set, get),
  setShowAllVariables: (showAll) => set({ showAllVariables: showAll }),
  requestOpenConfigurator: () =>
    set((s) => ({ configuratorOpenRequestTick: s.configuratorOpenRequestTick + 1 })),
  bumpMediaBank: () => set((s) => ({ mediaBankVersion: s.mediaBankVersion + 1 })),
  bumpShapeBank: () => set((s) => ({ shapeBankVersion: s.shapeBankVersion + 1 })),
  bumpCardBank: () => set((s) => ({ cardBankVersion: s.cardBankVersion + 1 })),
});

const actionMiddleware = (config) => (set, get, api) => {
  const result = config(
    async (partial, replace, ...args) => {
      // Exécuter la mise à jour de l'état
      set(partial, replace, ...args);

      // Après la mise à jour de l'état
      // Collab : un co-éditeur charge une section → on diffère (le
      // box_unlocked distant déclenchera un set qui relancera cette boucle)
      if (
        get().sectionsToActivate.length > 0 &&
        !get().areSectionsActivating &&
        !sectionLoadingHolder(get())
      ) {
        set(
          produce((draft) => {
            draft.areSectionsActivating = true;
          }),
          false,
          `areSectionsActivating begins`
        );
        while (get().sectionsToActivate.length > 0) {
          const sectionToActivate = get().sectionsToActivate[0];

          set(
            produce((draft) => {
              draft.sectionsToActivate.shift();
            }),
            `Remove section to activate`
          );
          await get().handleSectionAction(sectionToActivate);
        }
        set(
          produce((draft) => {
            draft.areSectionsActivating = false;
          }),
          `areSectionsActivating ends`
        );
      }
    },
    get,
    api
  );

  return result;
};

const isDevelopment = process.env.REACT_APP_DEVELOPPEMENT === 'true';

export const useStore = create(
  isDevelopment ? devtools(actionMiddleware(store)) : actionMiddleware(store)
);
